import { useCallback, useEffect, useMemo, useState } from "react";
import { StyledDocContainer } from "./styles";
import ReactCode from "./ReactCode";
import HtmlCode from "./HtmlCode";
import { useDispatch, useSelector } from "react-redux";
import { selectSelectedTenant } from "../../../store/user/selectors";
import { WIDGET_URL } from "../../../config";
import { getTenantDetailsById } from "../../../store/user/thunks";
const Documentation = () => {
  const selectedTenant = useSelector(selectSelectedTenant);

  const [activeTab, setActiveTab] = useState("react");
  const [copied, setCopied] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!selectedTenant?.industry && selectedTenant?.id)
      dispatch(
        getTenantDetailsById({
          tenant_id: selectedTenant?.id,
        }),
      );
  }, [selectedTenant?.id]);

  const botScriptURL = useMemo(() => {
    let url = `${WIDGET_URL}?clientId=${selectedTenant?.id}`;

    if (selectedTenant?.background_color) {
      url += `&primaryColor=${encodeURIComponent(selectedTenant.background_color)}`;
    }

    return url;
  }, [selectedTenant]);

  const handleCopy = useCallback(() => {
    const text =
      activeTab === "react"
        ? `  useEffect(() => {
    const script = document.createElement("script");
    script.src =
      "${botScriptURL}";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // @ts-ignore
      if (window?.KeluAiChatWidget) window.KeluAiChatWidget?.destroy();
    };
  }, []);`
        : `<script
  src="${botScriptURL}"
  async
></script>`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [activeTab, botScriptURL]);

  return (
    <StyledDocContainer>
      <div className="doc-hero">
        <div className="doc-hero-inner">
          <div className="doc-badge">
            <div className="doc-badge-dot" />
            Widget Integration
          </div>
          <h1 className="doc-title">
            Embed <span>Kelu AI</span> on your website
          </h1>
          <p className="doc-subtitle">
            Add the Kelu AI chat widget to any website in under 2 minutes.
            Choose the integration method that fits your stack.
          </p>
        </div>
      </div>

      <div className="doc-body">
        {/* Prerequisites */}
        <div className="doc-section">
          <div className="doc-section-header">
            <div className="doc-section-title">Before you begin</div>
          </div>
          <div className="doc-card">
            <table className="doc-param-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <code className="doc-tenant-highlight">clientId</code>
                  </td>
                  <td>
                    <span className="doc-tag-type">string</span>
                  </td>
                  <td
                    style={{
                      color: "#64748b",
                      fontFamily: "Inter, sans-serif",
                    }}
                  >
                    Your unique Client ID from the Kelu AI dashboard. Passed as
                    a URL query parameter to identify your account.
                  </td>
                  <td>
                    <span className="doc-tag-required">Required</span>
                  </td>
                </tr>
                <tr>
                  <td>
                    <code className="doc-tenant-highlight">primaryColor</code>
                  </td>
                  <td>
                    <span className="doc-tag-type">string</span>
                  </td>
                  <td
                    style={{
                      color: "#64748b",
                      fontFamily: "Inter, sans-serif",
                    }}
                  >
                    Optional hex color value used to customize the widget's
                    primary theme color. Example: <code>#0eef1d</code>. Passed
                    as a URL query parameter.
                  </td>
                  <td>
                    <span className="doc-tag-optional">Optional</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="doc-callout info" style={{ marginTop: 12 }}>
            <span className="doc-callout-icon">ℹ️</span>
            <div>
              <strong>Your Client ID</strong>
              Your <code className="doc-tenant-highlight">
                clientId
              </code> is{" "}
              <code className="doc-tenant-highlight">{selectedTenant?.id}</code>{" "}
              — replace this value in the snippets below with the one from your
              dashboard.
            </div>
          </div>
        </div>

        <div className="doc-divider" />

        {/* Integration */}
        <div className="doc-section">
          <div className="doc-section-header">
            <div className="doc-section-number">1</div>
            <div className="doc-section-title">Add the widget</div>
          </div>
          <p className="doc-section-desc">
            Choose the snippet that matches your setup. Both methods load the
            same widget — the React version handles cleanup automatically when
            the component unmounts.
          </p>

          <div className="doc-tabs">
            <button
              className={`doc-tab ${activeTab === "react" ? "active" : ""}`}
              onClick={() => setActiveTab("react")}
            >
              ⚛ React / Next.js
            </button>
            <button
              className={`doc-tab ${activeTab === "html" ? "active" : ""}`}
              onClick={() => setActiveTab("html")}
            >
              {"</>"} HTML / Script tag
            </button>
          </div>

          <div className="doc-card">
            <div className="doc-card-header">
              <div className="doc-card-lang">
                <div className="doc-card-dots">
                  <div className="doc-dot doc-dot-red" />
                  <div className="doc-dot doc-dot-yellow" />
                  <div className="doc-dot doc-dot-green" />
                </div>
                <span className="doc-lang-label">
                  {activeTab === "react"
                    ? "React — useEffect hook"
                    : "HTML — Script tag"}
                </span>
              </div>
              <button
                className={`doc-copy-btn ${copied ? "copied" : ""}`}
                onClick={handleCopy}
              >
                {copied ? "✓ Copied!" : "⎘ Copy"}
              </button>
            </div>
            <div className="doc-code">
              {activeTab === "react" ? (
                <ReactCode botScriptURL={botScriptURL} />
              ) : (
                <HtmlCode botScriptURL={botScriptURL} />
              )}
            </div>
          </div>

          {activeTab === "html" && (
            <div className="doc-callout tip" style={{ marginTop: 12 }}>
              <span className="doc-callout-icon">💡</span>
              <div>
                <strong>Placement tip</strong>
                Place the{" "}
                <code className="doc-tenant-highlight">&lt;script&gt;</code> tag
                just before the closing{" "}
                <code className="doc-tenant-highlight">&lt;/body&gt;</code> tag
                so the widget loads after your page content.
              </div>
            </div>
          )}

          {activeTab === "react" && (
            <div className="doc-callout tip" style={{ marginTop: 12 }}>
              <span className="doc-callout-icon">⚛</span>
              <div>
                <strong>Next.js users</strong>
                Place this{" "}
                <code className="doc-tenant-highlight">useEffect</code> in your
                root layout component (e.g.{" "}
                <code className="doc-tenant-highlight">app/layout.tsx</code>) to
                load the widget on every page.
              </div>
            </div>
          )}
        </div>

        <div className="doc-divider" />

        {/* Cleanup / Destroy */}
        <div className="doc-section">
          <div className="doc-section-header">
            <div className="doc-section-number">2</div>
            <div className="doc-section-title">Destroying the widget</div>
          </div>
          <p className="doc-section-desc">
            Kelu AI exposes a global{" "}
            <code className="doc-tenant-highlight">
              window.KeluAiChatWidget
            </code>{" "}
            object after the script loads. Use the{" "}
            <code className="doc-tenant-highlight">destroy()</code> method to
            cleanly remove the widget from the page.
          </p>

          <div className="doc-card">
            <div className="doc-card-header">
              <div className="doc-card-lang">
                <div className="doc-card-dots">
                  <div className="doc-dot doc-dot-red" />
                  <div className="doc-dot doc-dot-yellow" />
                  <div className="doc-dot doc-dot-green" />
                </div>
                <span className="doc-lang-label">Manual destroy</span>
              </div>
            </div>
            <div className="doc-code">
              <pre>
                <span className="c-cm">
                  // Call this anywhere to remove the widget
                </span>
                {"\n"}
                <span className="c-kw">if</span> {"(window?.KeluAiChatWidget)"}
                {"\n"}
                {"  "}window.<span className="c-var">KeluAiChatWidget</span>?.
                <span className="c-fn">destroy</span>
                {"()"};
              </pre>
            </div>
          </div>

          <div className="doc-callout warning" style={{ marginTop: 12 }}>
            <span className="doc-callout-icon">⚠️</span>
            <div>
              <strong>What destroy() does</strong>
              Calling{" "}
              <code className="doc-tenant-highlight">
                window.KeluAiChatWidget.destroy()
              </code>{" "}
              completely removes the widget UI, its event listeners, and the
              chat session from your page. This is called automatically in the
              React <code className="doc-tenant-highlight">useEffect</code>{" "}
              cleanup function when the component unmounts — for example, when
              navigating away from a page. If you're using the plain script tag,
              call it manually when you want to remove the widget.
            </div>
          </div>

          <div className="doc-callout info" style={{ marginTop: 12 }}>
            <span className="doc-callout-icon">ℹ️</span>
            <div>
              <strong>Optional chaining</strong>
              The <code className="doc-tenant-highlight">?.</code> optional
              chaining ensures no error is thrown if the widget script hasn't
              fully loaded yet when destroy is called — safe to use in all
              cases.
            </div>
          </div>
        </div>

        <div className="doc-divider" />

        {/* Global object reference */}
        <div className="doc-section">
          <div className="doc-section-header">
            <div className="doc-section-number">3</div>
            <div className="doc-section-title">Global API reference</div>
          </div>
          <p className="doc-section-desc">
            After the script loads, the following global object is available on{" "}
            <code className="doc-tenant-highlight">window</code>.
          </p>

          <div className="doc-card">
            <table className="doc-param-table">
              <thead>
                <tr>
                  <th>Property / Method</th>
                  <th>Type</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <code className="doc-tenant-highlight">
                      window.KeluAiChatWidget
                    </code>
                  </td>
                  <td>
                    <span className="doc-tag-type">object</span>
                  </td>
                  <td
                    style={{
                      color: "#64748b",
                      fontFamily: "Inter, sans-serif",
                    }}
                  >
                    The global widget instance injected after the script loads.
                  </td>
                </tr>
                <tr>
                  <td>
                    <code className="doc-tenant-highlight">.destroy()</code>
                  </td>
                  <td>
                    <span className="doc-tag-type">function</span>
                  </td>
                  <td
                    style={{
                      color: "#64748b",
                      fontFamily: "Inter, sans-serif",
                    }}
                  >
                    Removes the widget from the DOM and clears all event
                    listeners and session state.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </StyledDocContainer>
  );
};

export default Documentation;
